import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';

/*
 * Cloudflare Turnstile — invisible bot protection.
 *
 * A single hidden widget is mounted at the app root. Any component can call
 * useTurnstile().getToken() to obtain a fresh single-use token, which must be
 * attached as the X-Turnstile-Token header on every protected request.
 *
 * Tokens are single-use and expire after 5 minutes — always fetch a new one
 * per request instead of caching.
 */

const TurnstileContext = createContext(null);
const SCRIPT_ID = 'cloudflare-turnstile-script';
// Cloudflare's official "always-pass invisible" test site key.
// Preview + local dev use this; production overrides via REACT_APP_TURNSTILE_SITE_KEY.
const TEST_SITE_KEY = '1x00000000000000000000BB';
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || TEST_SITE_KEY;

// Fail-loud: if the site key is missing from env, warn on boot so a broken
// production deploy doesn't silently ship with test keys (which always pass
// and provide zero bot protection).
if (!process.env.REACT_APP_TURNSTILE_SITE_KEY) {
  // eslint-disable-next-line no-console
  console.warn(
    '[Turnstile] REACT_APP_TURNSTILE_SITE_KEY is not set — falling back to Cloudflare test key. ' +
    'Bot protection is EFFECTIVELY DISABLED. Set the real site key in .env before shipping to production.'
  );
} else if (SITE_KEY === TEST_SITE_KEY) {
  // eslint-disable-next-line no-console
  console.warn(
    '[Turnstile] Using Cloudflare TEST site key — bot protection is bypassed. Fine for dev/preview, DO NOT use in production.'
  );
}

export const TurnstileProvider = ({ children }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const waitersRef = useRef([]);
  const readyRef = useRef(false);

  const resolveWaiters = useCallback((token) => {
    const pending = waitersRef.current.splice(0);
    pending.forEach(({ resolve, reject }) => {
      if (token) resolve(token);
      else reject(new Error('Turnstile challenge did not return a token'));
    });
  }, []);

  useEffect(() => {
    const start = () => {
      if (!window.turnstile || !containerRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        // Explicit execute mode: no challenge runs on mount, only when getToken() calls execute().
        execution: 'execute',
        action: 'academy',
        appearance: 'execute',
        'refresh-expired': 'auto',
        callback: (token) => resolveWaiters(token),
        'expired-callback': () => {
          waitersRef.current.splice(0).forEach(({ reject }) => reject(new Error('Turnstile token expired')));
        },
        'error-callback': () => {
          waitersRef.current.splice(0).forEach(({ reject }) => reject(new Error('Turnstile widget error')));
        },
      });
      readyRef.current = true;
    };

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (window.turnstile) start();
      else existing.addEventListener('load', start, { once: true });
    } else {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = start;
      document.head.appendChild(script);
    }

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) { /* ignore */ }
        widgetIdRef.current = null;
      }
    };
  }, [resolveWaiters]);

  const getToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!window.turnstile || widgetIdRef.current === null) {
        // Widget not mounted yet — wait briefly then try once more
        const retry = setTimeout(() => {
          if (!window.turnstile || widgetIdRef.current === null) {
            reject(new Error('Bot check not ready. Please refresh the page.'));
            return;
          }
          _requestToken(resolve, reject);
        }, 800);
        // Also register a timeout in case retry itself hangs
        return () => clearTimeout(retry);
      }
      _requestToken(resolve, reject);
    });

    function _requestToken(resolve, reject) {
      const timer = setTimeout(() => {
        reject(new Error('Bot check timed out. Please try again.'));
      }, 10000);
      waitersRef.current.push({
        resolve: (t) => { clearTimeout(timer); resolve(t); },
        reject: (e) => { clearTimeout(timer); reject(e); },
      });
      try {
        window.turnstile.execute(widgetIdRef.current);
      } catch (err) {
        clearTimeout(timer);
        reject(err);
      }
    }
  }, []);

  return (
    <TurnstileContext.Provider value={{ getToken }}>
      {/* Invisible widget container — never visible to users */}
      <div ref={containerRef} aria-hidden="true" style={{ display: 'none' }} data-testid="turnstile-widget" />
      {children}
    </TurnstileContext.Provider>
  );
};

export const useTurnstile = () => {
  const value = useContext(TurnstileContext);
  if (!value) {
    // Non-throwing fallback so components in Storybook / tests without provider still work
    return {
      getToken: async () => 'no-turnstile-provider',
    };
  }
  return value;
};
